import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { RequireAuth, RequireOnboarding } from '@/components/layout/RouteGuards'
import { Toaster } from '@/components/ui/Feedback'
import { AuthPage } from '@/features/auth/AuthPage'
import { ApplicationReviewPage } from '@/features/applications/ApplicationReviewPage'
import { BillingCancelPage, BillingSuccessPage } from '@/features/billing/BillingReturnPages'
import { PlansPage } from '@/features/billing/PlansPage'
import { ReviewInboxPage } from '@/features/applications/ReviewInboxPage'
import { TrackerPage } from '@/features/applications/TrackerPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { AutoApplyPage } from '@/features/jobs/AutoApplyPage'
import { BrowseJobsPage } from '@/features/jobs/BrowseJobsPage'
import { OnboardingPage } from '@/features/onboarding/OnboardingPage'
import { PortalHandoffPage } from '@/features/profile/PortalHandoffPage'
import { ProfilePage } from '@/features/profile/ProfilePage'
import { SettingsPage } from '@/features/settings/SettingsPage'
import { GettingStartedPage } from '@/features/setup/GettingStartedPage'

// The War Room (interview prep) pages carry the code editor, diagrams and the
// interview room: loaded when first opened, not with the app.
const WarRoomPage = lazy(() => import('@/features/prep/pages/WarRoomPage'))
const WorkspacePage = lazy(() => import('@/features/prep/pages/WorkspacePage'))
const ProblemPage = lazy(() => import('@/features/prep/pages/ProblemPage'))
const PlaygroundPage = lazy(() => import('@/features/prep/pages/PlaygroundPage'))
const MockSetupPage = lazy(() => import('@/features/mock/pages/MockSetupPage'))
const MockRoomPage = lazy(() => import('@/features/mock/pages/MockRoomPage'))
const MockReportPage = lazy(() => import('@/features/mock/pages/MockReportPage'))

function Lazy({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<div className="flex min-h-[40vh] items-center justify-center text-[13px] text-ink-muted">Loading…</div>}>
      {children}
    </Suspense>
  )
}

export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />

        <Route element={<RequireAuth />}>
          <Route element={<RequireOnboarding expect="incomplete" />}>
            <Route path="/onboarding" element={<OnboardingPage />} />
          </Route>

          <Route element={<RequireOnboarding expect="complete" />}>
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="getting-started" element={<GettingStartedPage />} />
              <Route path="jobs" element={<BrowseJobsPage />} />
              <Route path="auto-apply" element={<AutoApplyPage />} />
              <Route path="tracker" element={<TrackerPage />} />
              <Route path="review" element={<ReviewInboxPage />} />
              <Route path="applications/:applicationId" element={<ApplicationReviewPage />} />
              <Route path="profile/:tab?" element={<ProfilePage />} />
              <Route path="portals/:key/handoff" element={<PortalHandoffPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="plans" element={<PlansPage />} />
              <Route path="billing/success" element={<BillingSuccessPage />} />
              <Route path="billing/cancel" element={<BillingCancelPage />} />
              <Route path="prep" element={<Lazy><WarRoomPage /></Lazy>} />
              <Route path="prep/mock/new" element={<Lazy><MockSetupPage /></Lazy>} />
              <Route path="prep/mock/:mockId" element={<Lazy><MockReportPage /></Lazy>} />
              <Route path="prep/:workspaceId/:tab?" element={<Lazy><WorkspacePage /></Lazy>} />
            </Route>
            {/* Full-screen War Room surfaces: the coding workspace, the playground, the interview room. */}
            <Route path="/prep/problems/:problemId" element={<Lazy><ProblemPage /></Lazy>} />
            <Route path="/prep/playground" element={<Lazy><PlaygroundPage /></Lazy>} />
            <Route path="/prep/interview/:mockId" element={<Lazy><MockRoomPage /></Lazy>} />
          </Route>
        </Route>

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster />
    </>
  )
}

function NotFound() {
  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-canvas px-4 text-center">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--brand) / 0.07) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--brand) / 0.07) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
          maskImage: 'radial-gradient(ellipse 50% 45% at 50% 50%, #000 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 50% 45% at 50% 50%, #000 20%, transparent 100%)',
        }}
      />
      <div className="relative flex w-full max-w-[420px] flex-col items-center animate-fade-in">
        <span className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-brand text-oncolor">
          <svg className="h-5 w-5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2 3.5h10M7 3.5V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <p className="num mt-6 rounded-pill bg-brand-soft px-3 py-1 text-[12px] font-medium text-brand">404</p>
        <h1 className="mt-4 text-[24px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[30px]">
          Page not found
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-ink-muted">That link does not go anywhere.</p>
        <a
          href="/dashboard"
          className="mt-7 inline-flex h-11 items-center justify-center rounded-field bg-ink px-5 text-[15px] font-medium text-oncolor transition-colors hover:bg-ink/90"
        >
          Back to dashboard
        </a>
      </div>
    </div>
  )
}
